import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save, RotateCcw, Lock } from "lucide-react";
import { ThermalRates } from "@/components/admin/ThermalRates";

const EDITABLE_KEYS = [
  ["capex_solar_per_kw", "Solar CAPEX ($/kW)", 1],
  ["capex_battery_per_kwh", "Battery CAPEX ($/kWh)", 1],
  ["capex_wind_per_kw", "Wind CAPEX ($/kW)", 1],
  ["diesel_fuel_price", "Diesel fuel price (USD/L)", 0.01],
  ["diesel_l_per_kwh", "Diesel L/kWh", 0.01],
  ["pr_default", "PV Performance Ratio (default)", 0.01],
  ["battery_dod", "Battery Depth of Discharge", 0.01],
  ["battery_round_trip", "Battery round-trip efficiency", 0.01],
  ["wind_cf_default", "Wind capacity factor", 0.01],
  ["reserve_default", "Reserve margin (default)", 0.01],
  ["reserve_reliability", "Reserve margin (reliability)", 0.01],
  ["bop_epc_pct", "BoP / EPC (% of subtotal)", 0.01],
  ["contingency_pct", "Contingency (% of subtotal)", 0.01],
  ["discount_rate", "Discount rate (nominal)", 0.01],
];

export const Admin = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [defaults, setDefaults] = useState({});
  const [overrides, setOverrides] = useState({});
  const [plausible, setPlausible] = useState("");
  const [initialPlausible, setInitialPlausible] = useState("");
  const [thermalDefaults, setThermalDefaults] = useState(null);
  const [thermalOverridesRaw, setThermalOverridesRaw] = useState("{}");
  const [thermalOverridesError, setThermalOverridesError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    api.get("/config").then((r) => {
      setDefaults(r.data.coefficients_default);
      setOverrides(r.data.coefficients_overrides || {});
      setPlausible(r.data.plausible_domain || "");
      setInitialPlausible(r.data.plausible_domain || "");
      setThermalDefaults(r.data.thermal_defaults || null);
      setThermalOverridesRaw(JSON.stringify(r.data.thermal_overrides || {}, null, 2));
    });
  }, [user, loading, navigate]);

  const setV = (k, v) => setOverrides((o) => ({ ...o, [k]: v }));
  const revert = (k) => setOverrides((o) => { const nx = { ...o }; delete nx[k]; return nx; });

  const save = async () => {
    setBusy(true);
    // Validate the thermal JSON before firing the PATCH — a syntax error here
    // should NOT wipe unrelated coefficient / plausible edits, so we short-circuit.
    let thermalParsed = null;
    if (thermalOverridesRaw && thermalOverridesRaw.trim() !== "") {
      try {
        thermalParsed = JSON.parse(thermalOverridesRaw);
      } catch (e) {
        setThermalOverridesError("Invalid JSON — fix the syntax and save again.");
        toast.error("Thermal overrides JSON is invalid");
        setBusy(false);
        return;
      }
    } else {
      thermalParsed = {};
    }
    setThermalOverridesError("");
    try {
      // Coefficient overrides are locked until after launch (the API refuses a non-empty map).
      const payload = { thermal: thermalParsed };
      if (plausible !== initialPlausible) payload.plausible_domain = plausible;
      await api.patch("/config", payload);
      setInitialPlausible(plausible);
      toast.success("Settings saved");
    } catch (e) {
      if (e.response?.status === 403) toast.error("Admin access required — ask an admin to add your email to ADMIN_EMAILS.");
      else toast.error("Save failed");
    }
    setBusy(false);
  };

  const isAdmin = user?.is_admin !== false;

  return (
    <div className="App" style={{ background: "var(--paper)" }}>
      <Header />
      <div className="max-w-[1080px] mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap justify-between items-end gap-4 mb-6">
          <div>
            <div className="overline mb-2">Admin</div>
            <h1 className="font-display text-3xl">Configurator coefficients & site settings</h1>
            <p className="text-sm mt-2" style={{ color: "var(--muted)" }}>
              Overrides apply immediately to new configurator runs. Set <span className="font-mono">ADMIN_EMAILS</span> in the backend .env to gate this page to a specific allow-list.
            </p>
          </div>
          <button onClick={save} disabled={busy || !isAdmin} className="btn-primary" data-testid="admin-save">
            {isAdmin ? <><Save size={13} /> {busy ? "Saving…" : "Save changes"}</> : <><Lock size={13} /> Admin only</>}
          </button>
        </div>

        {!isAdmin && (
          <div className="card p-5 mb-6" style={{ background: "#fff8f2", borderColor: "#ffd7c2", color: "#8a3a13" }}>
            You are signed in but not on the admin allow-list. Contact your administrator to add your email to <span className="font-mono">ADMIN_EMAILS</span>.
          </div>
        )}

        {/* Site settings */}
        <div className="card p-6 md:p-8 mb-6" data-testid="admin-site">
          <div className="overline mb-3">Site settings</div>
          <h2 className="font-display text-xl mb-4">Analytics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <label className="label-field">Plausible domain (e.g. powerconfigurator.futureenergy.com)</label>
              <input
                type="text"
                className="field-input"
                placeholder="Leave empty to disable analytics"
                value={plausible}
                onChange={(e) => setPlausible(e.target.value)}
                disabled={!isAdmin}
                data-testid="admin-plausible-input"
              />
              <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>
                When set, the site injects the Plausible script and fires events: first_pass_run, signup, report_purchase, talk_to_engineer_click.
              </div>
            </div>
            <div className="p-4 rounded-md" style={{ background: "var(--paper)" }}>
              <div className="overline" style={{ fontSize: "0.62rem" }}>Currently active</div>
              <div className="font-mono mt-1" style={{ color: "var(--charcoal)" }}>{initialPlausible || "— unset —"}</div>
            </div>
          </div>
        </div>

        <ThermalRates defaults={thermalDefaults} raw={thermalOverridesRaw}
          onChange={setThermalOverridesRaw} disabled={!isAdmin} />

        {/* Thermal generation config (iter43) — additive, config-driven.
             We render the defaults read-only for reference and let the admin
             paste JSON overrides. A pretty per-field form would be nice
             later, but the JSON textarea is the safest zero-schema-lock-in
             editor for a nested config that may grow. */}
        <div className="card p-6 md:p-8 mb-6" data-testid="admin-thermal">
          <div className="overline mb-3">Thermal add-on</div>
          <h2 className="font-display text-xl mb-2">Gas engines · OCGT · CCGT · CHP · gas fuel · ambient</h2>
          <p className="text-sm mb-4" style={{ color: "var(--muted)" }}>
            Screening-level defaults. Any key you set below <span className="font-mono">deep-merges</span> onto the defaults;
            omitted keys stay on the defaults. Leave the JSON as <span className="font-mono">{"{}"}</span> to use every default.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label-field">Overrides (JSON)</label>
              <textarea
                className="field-input font-mono text-xs"
                style={{ minHeight: "260px" }}
                value={thermalOverridesRaw}
                onChange={(e) => setThermalOverridesRaw(e.target.value)}
                disabled={!isAdmin}
                data-testid="admin-thermal-overrides"
              />
              {thermalOverridesError && (
                <div className="text-xs mt-1" style={{ color: "#873F00" }} data-testid="admin-thermal-error">{thermalOverridesError}</div>
              )}
            </div>
            <div>
              <label className="label-field">Defaults (read-only)</label>
              <pre className="p-3 rounded font-mono text-xs overflow-auto"
                    style={{ background: "var(--paper)", border: "1px solid var(--hairline)", maxHeight: "260px" }}
                    data-testid="admin-thermal-defaults">
                {thermalDefaults ? JSON.stringify(thermalDefaults, null, 2) : "Loading…"}
              </pre>
            </div>
          </div>
        </div>

        {/* Coefficients */}
        <div className="card p-6 md:p-8">
          <div className="overline mb-3">Configurator coefficients</div>
          <h2 className="font-display text-xl mb-4">CAPEX, OPEX, sizing & finance defaults</h2>
          <div className="p-3 mb-4 rounded-md text-sm" style={{ background: "var(--paper)", border: "1px solid var(--hairline)" }} data-testid="admin-coefficients-locked">
            <p>Configurator coefficient overrides are disabled until after launch: they would bypass the scored configurator. The defaults apply.</p>
            <p className="mt-1" style={{ color: "var(--muted)" }}>Les ajustements des coefficients du configurateur sont désactivés jusqu'après le lancement : ils contourneraient le configurateur évalué. Les valeurs par défaut s'appliquent.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {EDITABLE_KEYS.map(([k, label, step]) => {
              const defV = defaults[k];
              const cur = overrides[k] !== undefined ? overrides[k] : defV;
              const modified = overrides[k] !== undefined && overrides[k] !== defV;
              return (
                <div key={k} className="p-4 rounded-md" style={{ background: "var(--paper)", border: modified ? "1px solid var(--brand-green)" : "1px solid var(--hairline)" }}>
                  <label className="label-field flex items-center justify-between">
                    <span>{label}</span>
                    {modified && (
                      <button onClick={() => revert(k)} className="btn-ghost !p-0 text-xs" data-testid={`revert-${k}`}>
                        <RotateCcw size={11} className="inline" /> revert
                      </button>
                    )}
                  </label>
                  <input
                    type="number"
                    step={step}
                    className="field-input font-mono"
                    value={cur ?? ""}
                    onChange={(e) => setV(k, parseFloat(e.target.value) || 0)}
                    disabled
                    readOnly
                    data-testid={`admin-input-${k}`}
                  />
                  <div className="text-xs mt-1 font-mono" style={{ color: "var(--muted)" }}>default {defV}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};
